import { FadeUp, AnimatedLine, StaggerContainer, StaggerItem } from "../components/Animations";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

const UAEPage = () => {
  const services = [
    {
      title: "Strategic Advisory",
      description: "Tailored investment insights and strategic guidance for small and medium-sized businesses seeking growth and operational excellence.",
      details: [
        "Market analysis & positioning",
        "Growth strategy development",
        "Operational optimization",
        "Performance improvement"
      ]
    },
    {
      title: "Capital Advisory",
      description: "Comprehensive capital structuring and financing solutions designed to support business expansion and strategic initiatives.",
      details: [
        "Capital structuring",
        "Financing solutions",
        "Investment evaluation",
        "Deal structuring"
      ]
    },
    {
      title: "Commercial Management",
      description: "End-to-end commercial management services supporting SMEs, commercial enterprises, and real estate projects across diverse sectors.",
      details: [
        "Property management",
        "Commercial operations",
        "Project oversight",
        "Asset optimization"
      ]
    },
  ];

  const capabilities = [
    {
      title: "Investment Management",
      description: "Strategic capital deployment across diversified portfolio companies"
    },
    {
      title: "Project Evaluation",
      description: "Rigorous assessment and due diligence for investment opportunities"
    },
    {
      title: "Property Management",
      description: "Self-owned property management and real estate optimization"
    },
    {
      title: "SME Advisory",
      description: "Sector expertise from approved professional partners"
    }
  ];

  return (
    <div data-testid="uae-page">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="container-wide">
          <FadeUp>
            <Link 
              to="/portfolio" 
              className="inline-flex items-center gap-2 text-stone hover:text-charcoal transition-colors duration-300 mb-8"
              data-testid="back-to-portfolio"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-sans uppercase tracking-wider">Global Portfolio</span>
            </Link>
          </FadeUp>

          <FadeUp delay={0.1}>
            <span className="text-caption text-bronze mb-6 block">United Arab Emirates</span>
            <h1 className="text-display text-charcoal max-w-5xl">
              2 ON Capital — FZCO
            </h1>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-12 max-w-3xl">
            <p className="text-xl md:text-2xl font-sans font-light text-stone leading-relaxed">
              A knowledge-based investment and management firm providing strategic advisory 
              and operational support to small and medium-sized businesses.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Hero Image */}
      <section className="pb-24 md:pb-32">
        <div className="container-wide">
          <FadeUp>
            <div className="aspect-[21/9] relative overflow-hidden border-glass">
              <img
                src="https://images.unsplash.com/photo-1770546597346-adb6f350812c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxzYW5kJTIwZHVuZXMlMjBtaW5pbWFsJTIwYmVpZ2V8ZW58MHx8fHwxNzcwNzY2OTY4fDA&ixlib=rb-4.1.0&q=85"
                alt="Dubai headquarters"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-bronze/5" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Dubai Headquarters */}
      <section className="section-spacing bg-charcoal">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <span className="text-caption text-bronze mb-6 block">Headquarters</span>
              <h2 className="text-headline text-champagne mb-8">
                Dubai — Investment & Commercial Management
              </h2>
              <p className="text-lg font-sans font-light text-champagne/70 mb-6">
                2 ON Capital FZCO operates as the strategic hub of our group, delivering 
                tailored investment insights, project evaluation, and capital advisory services.
              </p>
              <p className="text-body text-champagne/60 mb-6">
                We provide commercial management solutions across SMEs, commercial enterprises, 
                and real estate projects, supported by sector expertise from approved professional partners.
              </p>
              <a 
                href="https://www.2oncapital.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-bronze hover:text-champagne transition-colors duration-300"
              >
                <span className="text-sm font-sans uppercase tracking-wider">www.2oncapital.com</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-8 card-glass-dark">
                  <span className="text-3xl font-serif text-bronze">FZCO</span>
                  <p className="text-sm text-champagne/60 mt-2">Free Zone Entity</p>
                </div>
                <div className="p-8 card-glass-dark">
                  <span className="text-3xl font-serif text-bronze">SME</span>
                  <p className="text-sm text-champagne/60 mt-2">Focus Segment</p>
                </div>
                <div className="p-8 card-glass-dark">
                  <span className="text-3xl font-serif text-bronze">3</span>
                  <p className="text-sm text-champagne/60 mt-2">Global Regions</p>
                </div>
                <div className="p-8 card-glass-dark">
                  <span className="text-3xl font-serif text-bronze">8+</span>
                  <p className="text-sm text-champagne/60 mt-2">Portfolio Companies</p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="section-spacing bg-white grain">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Core Services</span>
            <h2 className="text-headline text-charcoal mb-16">
              Strategic support for growing businesses.
            </h2>
          </FadeUp>

          <StaggerContainer className="space-y-0" staggerDelay={0.15}>
            {services.map((service, index) => (
              <StaggerItem key={service.title}>
                <AnimatedLine delay={index * 0.1} />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-12">
                  <div className="md:col-span-1">
                    <span className="text-mono text-stone/50">0{index + 1}</span>
                  </div>
                  <div className="md:col-span-4">
                    <h3 className="text-2xl font-serif text-charcoal">{service.title}</h3>
                  </div>
                  <div className="md:col-span-6 md:col-start-7">
                    <p className="text-body mb-6">{service.description}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {service.details.map((detail, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-bronze flex-shrink-0" />
                          <span className="text-sm text-stone">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
            <AnimatedLine delay={0.4} />
          </StaggerContainer>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-spacing">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <FadeUp>
                <span className="text-caption text-bronze mb-6 block">Capabilities</span>
                <h2 className="text-headline text-charcoal mb-8">
                  Knowledge-Based Investment
                </h2>
              </FadeUp>

              <FadeUp delay={0.1}>
                <p className="text-lg font-sans font-light text-stone mb-6">
                  Our investment approach combines deep sector expertise with 
                  rigorous analytical frameworks to identify and support 
                  high-potential opportunities.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <p className="text-body">
                  We work alongside portfolio companies as active partners, 
                  providing not just capital but strategic guidance, 
                  operational support, and access to our network of 
                  professional partners.
                </p>
              </FadeUp>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <FadeUp delay={0.2}>
                <div className="space-y-6">
                  {capabilities.map((capability, index) => (
                    <div key={index} className="p-8 border-glass card-reflect transition-all duration-500">
                      <h4 className="font-serif text-xl text-charcoal mb-3">{capability.title}</h4>
                      <p className="text-body text-sm">{capability.description}</p>
                    </div>
                  ))}
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Cross Links */}
      <section className="section-spacing bg-sand/30">
        <div className="container-wide">
          <FadeUp>
            <h3 className="text-headline text-charcoal mb-12 text-center">
              Explore Our Portfolio Companies
            </h3>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FadeUp delay={0.1}>
              <Link 
                to="/portfolio/uk" 
                className="group block border-glass card-reflect shimmer p-8 transition-all duration-500"
                data-testid="link-to-uk"
              >
                <span className="text-mono text-stone/60 mb-2 block">United Kingdom</span>
                <h4 className="text-2xl font-serif text-charcoal group-hover:text-bronze transition-colors duration-500 mb-4">
                  Media, Automotive & Hospitality
                </h4>
                <p className="text-body text-sm mb-4">Film production, automotive services, and dining experiences</p>
                <span className="inline-flex items-center gap-2 text-bronze text-sm">
                  Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </FadeUp>
            <FadeUp delay={0.2}>
              <Link 
                to="/portfolio/pakistan" 
                className="group block border-glass card-reflect shimmer p-8 transition-all duration-500"
                data-testid="link-to-pakistan"
              >
                <span className="text-mono text-stone/60 mb-2 block">Pakistan</span>
                <h4 className="text-2xl font-serif text-charcoal group-hover:text-bronze transition-colors duration-500 mb-4">
                  Agriculture & Poultry
                </h4>
                <p className="text-body text-sm mb-4">Poultry farming, breeding, and agricultural infrastructure</p>
                <span className="inline-flex items-center gap-2 text-bronze text-sm">
                  Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 border-t border-sand">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
              data-testid="nav-back-portfolio"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-sans font-medium uppercase tracking-wider">Back to Portfolio</span>
            </Link>

            <Link
              to="/portfolio/uk"
              className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
              data-testid="nav-next-uk"
            >
              <span className="text-sm font-sans font-medium uppercase tracking-wider">United Kingdom</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UAEPage;
